class PMCustomStickersOrderSummary extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId || '';
    this.currency = this.dataset.currency || 'EUR';

    this.productInfo =
      document.getElementById(`ProductInfo-${this.sectionId}`) ||
      this.closest('.product')?.querySelector('.product__info-container') ||
      document.querySelector('.product__info-container');

    this.priceRoot =
      document.getElementById(`price-${this.sectionId}`) ||
      this.productInfo?.querySelector('[id^="price-"]') ||
      document.querySelector('[id^="price-"]');

    this.values = {
      shape: this.querySelector('[data-pm-summary-value="shape"]'),
      size: this.querySelector('[data-pm-summary-value="size"]'),
      quantity: this.querySelector('[data-pm-summary-value="quantity"]'),
      finish: this.querySelector('[data-pm-summary-value="finish"]'),
      unitPrice: this.querySelector('[data-pm-summary-value="unit-price"]'),
      totalPrice: this.querySelector('[data-pm-summary-value="total-price"]'),
    };

    this.onChange = () => {
      this.clearLpoPriceCache();
      this.scheduleUpdate();
      this.scheduleDelayedUpdate();
    };

    this.onLpoPrice = (event) => {
      this.handleLpoPrice(event);
    };

    this.onMutation = () => {
      this.scheduleUpdate();
    };

    document.addEventListener('change', this.onChange, true);
    document.addEventListener('input', this.onChange, true);
    document.addEventListener('click', this.onChange, true);
    document.addEventListener('lpo:price', this.onLpoPrice);

    this.setupObservers();

    /*
      Nie zostawiamy summary na stałe hidden.
      JS spróbuje je jeszcze prawidłowo przenieść.
    */
    this.removeAttribute('hidden');

    this.scheduleUpdate();
    this.scheduleDelayedUpdate();
  }


  disconnectedCallback() {
    document.removeEventListener('change', this.onChange, true);
    document.removeEventListener('input', this.onChange, true);
    document.removeEventListener('click', this.onChange, true);
    document.removeEventListener('lpo:price', this.onLpoPrice);

    this.productObserver?.disconnect();
    this.priceObserver?.disconnect();
    this.stickyPriceObserver?.disconnect();

    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
    }

    window.clearTimeout(this.delayedUpdateId);
    window.clearTimeout(this.finalUpdateId);
  }


  /* =========================================================
     OBSERVERS
     ========================================================= */

  setupObservers() {
    if (this.productInfo) {
      this.productObserver = new MutationObserver(this.onMutation);

      this.productObserver.observe(this.productInfo, {
        attributes: true,
        attributeFilter: [
          'class',
          'aria-checked',
          'aria-selected',
          'aria-pressed',
          'checked',
          'selected',
        ],
        childList: true,
        subtree: true,
      });
    }

    if (this.priceRoot) {
      this.priceObserver = new MutationObserver(this.onMutation);

      this.priceObserver.observe(this.priceRoot, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    this.observeStickyPrice();
  }


  observeStickyPrice() {
    const stickyPrice =
      document.querySelector('[data-pm-sticky-price]');

    if (!stickyPrice || this.stickyPriceObserver) {
      return;
    }

    this.stickyPriceObserver =
      new MutationObserver(this.onMutation);

    this.stickyPriceObserver.observe(stickyPrice, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }


  /* =========================================================
     LPO PRICE
     ========================================================= */

  handleLpoPrice(event) {
    const price = event?.detail?.price;

    if (
      typeof price === 'string' &&
      price.trim()
    ) {
      this.lastLpoPriceText = price.trim();
      this.lastLpoPriceNumber =
        this.parseMoney(this.lastLpoPriceText);
    }

    else if (
      typeof price === 'number' &&
      Number.isFinite(price)
    ) {
      this.lastLpoPriceNumber =
        price > 999
          ? price / 100
          : price;

      this.lastLpoPriceText =
        this.formatCurrency(
          this.lastLpoPriceNumber
        );
    }

    this.scheduleUpdate();
    this.scheduleDelayedUpdate();
  }


  clearLpoPriceCache() {
    this.lastLpoPriceText = '';
    this.lastLpoPriceNumber = null;
  }


  /* =========================================================
     UPDATE SCHEDULING
     ========================================================= */

  scheduleUpdate() {
    if (this.rafId) {
      return;
    }

    this.rafId =
      window.requestAnimationFrame(() => {
        this.rafId = null;

        this.placeSummary();
        this.updateSummary();
      });
  }


  scheduleDelayedUpdate() {
    window.clearTimeout(
      this.delayedUpdateId
    );

    window.clearTimeout(
      this.finalUpdateId
    );

    this.delayedUpdateId =
      window.setTimeout(() => {
        this.clearLpoPriceCache();
        this.scheduleUpdate();
      }, 300);

    this.finalUpdateId =
      window.setTimeout(() => {
        this.clearLpoPriceCache();
        this.scheduleUpdate();
      }, 800);
  }


  /* =========================================================
     SUMMARY POSITION
     ========================================================= */

  placeSummary() {
    /*
      Jeśli summary już jest w prawidłowym miejscu,
      nic więcej nie robimy.
    */
    if (
      this.hasAttribute(
        'data-pm-placed'
      )
    ) {
      this.removeAttribute('hidden');
      return;
    }


    /*
      1. Preferowana pozycja:
      przed uploadem
    */
    const uploadBlock =
      this.findUploadBlock();

    if (
      uploadBlock &&
      uploadBlock.parentElement
    ) {
      uploadBlock.parentElement.insertBefore(
        this,
        uploadBlock
      );

      this.removeAttribute('hidden');

      this.setAttribute(
        'data-pm-placed',
        'true'
      );

      return;
    }


    /*
      2. Fallback:
      przed główną ceną Dawn
    */
    if (
      this.priceRoot &&
      this.priceRoot.parentElement
    ) {
      this.priceRoot.parentElement.insertBefore(
        this,
        this.priceRoot
      );

      this.removeAttribute('hidden');

      this.setAttribute(
        'data-pm-placed',
        'true'
      );

      return;
    }


    /*
      3. Ostateczny fallback:
      nie chowaj summary.
    */
    this.removeAttribute('hidden');
  }


  /* =========================================================
     UPDATE SUMMARY
     ========================================================= */

  updateSummary() {
    const shape =
      this.readShape();

    const size =
      this.readSize(shape);

    const quantity =
      this.readQuantity();

    const finish =
      this.readFinish();

    const totalText =
      this.readTotalPriceText();

    const totalNumber =
      this.parseMoney(totalText);

    const quantityNumber =
      this.parseQuantity(quantity);


    this.setValue(
      'shape',
      shape
    );

    this.setValue(
      'size',
      size
    );

    this.setValue(
      'quantity',
      quantity
    );

    this.setValue(
      'finish',
      finish
    );

    this.setValue(
      'totalPrice',
      totalText
    );


    if (
      totalNumber &&
      quantityNumber
    ) {
      this.setValue(
        'unitPrice',
        this.formatCurrency(
          totalNumber /
          quantityNumber
        )
      );
    }

    else {
      this.setValue(
        'unitPrice',
        '-'
      );
    }
  }


  setValue(key, value) {
    if (!this.values[key]) {
      return;
    }

    this.values[key].textContent =
      value || '-';
  }


  /* =========================================================
     SHAPE
     handle: shape
     ========================================================= */

  readShape() {
    return (
      this.readOptionByHandle('shape') ||
      '-'
    );
  }


  /* =========================================================
     SIZE
     ========================================================= */

  readSize(shape) {
    const normalizedShape =
      this.normalize(shape);


    /*
      Circle / Square
    */
    if (
      normalizedShape.includes('circle') ||
      normalizedShape.includes('square')
    ) {
      const preset =
        this.readOptionByHandle(
          'sticker_size'
        );

      if (
        preset &&
        !/custom/i.test(preset)
      ) {
        return preset;
      }


      const custom =
        this.readOptionByHandle(
          'custom_size_single'
        );

      if (custom) {
        return custom;
      }
    }


    /*
      Rectangle / Custom Shape
    */
    if (
      normalizedShape.includes('rectangle') ||
      normalizedShape.includes('custom')
    ) {
      const preset =
        this.readOptionByHandle(
          'sticker_size'
        );

      if (
        preset &&
        !/custom/i.test(preset)
      ) {
        return preset;
      }


      const dimension =
        this.readDimensionOption(
          'custom_size_dimension'
        );

      if (dimension) {
        return dimension;
      }
    }


    /*
      Fallback
    */
    return (
      this.readOptionByHandle(
        'sticker_size'
      ) ||
      this.readOptionByHandle(
        'custom_size_single'
      ) ||
      this.readDimensionOption(
        'custom_size_dimension'
      ) ||
      '-'
    );
  }


  /* =========================================================
     QUANTITY
     ========================================================= */

  readQuantity() {
    let value =
      this.readOptionByHandle(
        'quantity_pack'
      );


    /*
      Standardowe pakiety
    */
    if (
      value &&
      !/custom/i.test(value)
    ) {
      const qty =
        this.parseQuantity(value);

      if (qty) {
        return `${qty} stickers`;
      }

      return value;
    }


    /*
      Custom quantity
    */
    const small =
      this.readInputValueByProperty(
        'custom_quantity_small'
      );

    const large =
      this.readInputValueByProperty(
        'custom_quantity_large'
      );


    const custom =
      small || large;


    const qty =
      this.parseQuantity(custom);


    if (qty) {
      return `${qty} stickers`;
    }


    return value || '-';
  }


  /* =========================================================
     FINISH
     ========================================================= */

  readFinish() {
    return (
      this.readOptionByHandle(
        'sticker_finish'
      ) ||
      '-'
    );
  }


  /* =========================================================
     READ LPO OPTION BY HANDLE
     ========================================================= */

  readOptionByHandle(handle) {
    const root =
      document.querySelector(
        `.cl-po--option[data-option="${CSS.escape(handle)}"]`
      );

    if (!root) {
      return '';
    }


    /*
      SELECT
    */
    const select =
      root.querySelector('select');

    if (
      select &&
      select.selectedOptions?.[0]
    ) {
      return this.cleanValue(
        select.selectedOptions[0].textContent
      );
    }


    /*
      RADIO / CHECKBOX
    */
    const checked =
      root.querySelector(
        'input[type="radio"]:checked,' +
        'input[type="checkbox"]:checked'
      );

    if (checked) {
      return this.cleanValue(
        this.getInputLabelText(checked)
      );
    }


    /*
      Image swatches / buttons
    */
    const active =
      this.findActiveValue(root);

    if (active) {
      return this.cleanValue(active);
    }


    /*
      Number / text input
    */
    const input =
      Array.from(
        root.querySelectorAll(
          'input:not([type="hidden"]):not([type="file"]), textarea'
        )
      ).find(
        (el) =>
          el.value &&
          el.value.trim()
      );

    if (input) {
      return this.cleanValue(
        input.value
      );
    }


    /*
      data-selected-value
    */
    const selectedValue =
      root.querySelector(
        '[data-selected-value]'
      );

    if (selectedValue) {
      return this.cleanValue(
        selectedValue.getAttribute(
          'data-selected-value'
        ) ||
        selectedValue.textContent
      );
    }


    return '';
  }


  /* =========================================================
     CUSTOM DIMENSION X / Y
     ========================================================= */

  readDimensionOption(handle) {
    const root =
      document.querySelector(
        `.cl-po--option[data-option="${CSS.escape(handle)}"]`
      );

    if (!root) {
      return '';
    }


    const inputs =
      Array.from(
        root.querySelectorAll(
          'input:not([type="hidden"]):not([type="file"])'
        )
      ).filter(
        (input) =>
          input.value &&
          input.value.trim()
      );


    if (!inputs.length) {
      return '';
    }


    const values =
      inputs
        .map(
          (input) =>
            this.cleanValue(
              input.value
            )
        )
        .filter(Boolean);


    if (
      values.length >= 2
    ) {
      return `${values[0]} × ${values[1]}`;
    }


    return values[0] || '';
  }


  /* =========================================================
     FORM PROPERTY INPUT
     ========================================================= */

  readInputValueByProperty(handle) {
    const selectors = [
      `input[name="properties[${CSS.escape(handle)}]"]`,
      `input[name="${CSS.escape(handle)}"]`,
      `.cl-po--option[data-option="${CSS.escape(handle)}"] input`,
    ];


    for (
      const selector of selectors
    ) {
      const input =
        document.querySelector(
          selector
        );

      if (
        input &&
        input.value &&
        input.value.trim()
      ) {
        return input.value.trim();
      }
    }


    return '';
  }


  /* =========================================================
     ACTIVE VALUE / IMAGE SWATCHES
     ========================================================= */

  findActiveValue(group) {
    const selectors = [
      'input:checked + label',
      'button[aria-pressed="true"]',
      'button[aria-selected="true"]',
      '[role="radio"][aria-checked="true"]',
      '[role="option"][aria-selected="true"]',

      '.cl-po--swatch[class*="selected" i]',
      '.cl-po--swatch[class*="active" i]',

      '.cl-po--radio-item[class*="selected" i]',
      '.cl-po--radio-item[class*="active" i]',

      '[data-value][aria-selected="true"]',
      '[data-value][aria-checked="true"]',
    ];


    for (
      const selector of selectors
    ) {
      const element =
        group.querySelector(
          selector
        );

      if (!element) {
        continue;
      }


      const value =
        element.getAttribute(
          'data-value'
        ) ||
        element.getAttribute(
          'data-label'
        ) ||
        element.getAttribute(
          'aria-label'
        ) ||
        element.textContent;


      if (
        value &&
        value.trim()
      ) {
        return value.trim();
      }
    }


    /*
      Fallback dla Image Swatches:
      szukamy checked input i jego label.
    */
    const checked =
      group.querySelector(
        'input:checked'
      );


    if (checked) {
      return this.getInputLabelText(
        checked
      );
    }


    return '';
  }


  getInputLabelText(input) {
    if (!input) {
      return '';
    }


    let explicitLabel = null;


    if (
      input.id &&
      this.productInfo
    ) {
      try {
        explicitLabel =
          this.productInfo.querySelector(
            `label[for="${CSS.escape(input.id)}"]`
          );
      }

      catch (error) {}
    }


    const wrappingLabel =
      input.closest('label');


    const item =
      input.closest(
        '.cl-po--radio-item,' +
        '.cl-po--swatch,' +
        '[data-value]'
      );


    return (
      explicitLabel?.getAttribute(
        'data-value'
      ) ||
      explicitLabel?.textContent ||
      wrappingLabel?.getAttribute(
        'data-value'
      ) ||
      wrappingLabel?.textContent ||
      item?.getAttribute(
        'data-value'
      ) ||
      item?.textContent ||
      input.getAttribute(
        'aria-label'
      ) ||
      (
        input.value &&
        ![
          'on',
          'true',
          'false'
        ].includes(
          input.value.toLowerCase()
        )
          ? input.value
          : ''
      )
    );
  }


  /* =========================================================
     FIND UPLOAD BLOCK
     ========================================================= */

  findUploadBlock() {
    /*
      Najpierw dokładnie po handle.
    */

    const primaryUpload =
      document.querySelector(
        '.cl-po--option[data-option="upload"]'
      );


    if (
      primaryUpload &&
      this.isVisible(primaryUpload)
    ) {
      return primaryUpload;
    }


    const rectUpload =
      document.querySelector(
        '.cl-po--option[data-option="upload_rect"]'
      );


    if (
      rectUpload &&
      this.isVisible(rectUpload)
    ) {
      return rectUpload;
    }


    /*
      Jeśli oba są niewidoczne w chwili startu,
      bierzemy pierwszy istniejący.
    */

    return (
      primaryUpload ||
      rectUpload ||
      null
    );
  }


  isVisible(element) {
    if (!element) {
      return false;
    }


    const style =
      window.getComputedStyle(
        element
      );


    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden'
    );
  }


  /* =========================================================
     TOTAL PRICE
     ========================================================= */

  readTotalPriceText() {
    const displayed =
      this.getCurrentDisplayedPriceText();


    return (
      displayed ||
      this.lastLpoPriceText ||
      '-'
    );
  }


  getCurrentDisplayedPriceText() {
    const selectors = [
      '.cl-po--total-price',
      '.cl-po--price-total',

      '[class*="total" i][class*="price" i]',

      '[data-live-price-quantity] .price-item--sale',
      '[data-live-price-quantity] .price-item--regular',

      '[data-pm-sticky-price]',

      '.product__info-container .price-item--sale',
      '.product__info-container .price-item--regular',
    ];


    for (
      const selector of selectors
    ) {
      const nodes =
        Array.from(
          document.querySelectorAll(
            selector
          )
        ).filter(
          (node) => {

            if (
              this.contains(node)
            ) {
              return false;
            }

            if (
              node.closest(
                'pm-custom-stickers-order-summary'
              )
            ) {
              return false;
            }


            return (
              this.parseMoney(
                node.textContent
              ) !== null
            );
          }
        );


      /*
        Bierzemy ostatni widoczny element.
      */
      const visibleNodes =
        nodes.filter(
          (node) =>
            node.offsetParent !== null
        );


      const node =
        visibleNodes[
          visibleNodes.length - 1
        ] ||
        nodes[
          nodes.length - 1
        ];


      const text =
        node?.textContent?.trim();


      if (text) {
        return text;
      }
    }


    return '';
  }


  /* =========================================================
     HELPERS
     ========================================================= */

  cleanValue(value) {
    let cleaned =
      String(value || '')
        .replace(/\s+/g, ' ')
        .trim();


    if (!cleaned) {
      return '';
    }


    /*
      Usuń dopłatę typu:
      + €3.00
    */
    cleaned =
      cleaned.replace(
        /\s*\+\s*[€$£]\s*\d+(?:[.,]\d{1,2})?.*$/i,
        ''
      ).trim();


    return this.dedupeRepeatedValue(
      cleaned
    );
  }


  dedupeRepeatedValue(value) {
    const parts =
      String(value || '')
        .split(/\s+/)
        .filter(Boolean);


    if (
      parts.length > 1 &&
      parts.length % 2 === 0
    ) {
      const midpoint =
        parts.length / 2;


      const firstHalf =
        parts
          .slice(
            0,
            midpoint
          )
          .join(' ');


      const secondHalf =
        parts
          .slice(
            midpoint
          )
          .join(' ');


      if (
        this.normalize(
          firstHalf
        ) ===
        this.normalize(
          secondHalf
        )
      ) {
        return firstHalf;
      }
    }


    return value;
  }


  parseQuantity(value) {
    const match =
      String(value || '')
        .match(
          /\d+(?:[.,]\d+)?/
        );


    if (!match) {
      return null;
    }


    const quantity =
      Number(
        match[0].replace(
          ',',
          '.'
        )
      );


    return (
      Number.isFinite(quantity) &&
      quantity > 0
    )
      ? quantity
      : null;
  }


  parseMoney(value) {
    if (
      this.lastLpoPriceText &&
      value === this.lastLpoPriceText &&
      this.lastLpoPriceNumber
    ) {
      return this.lastLpoPriceNumber;
    }


    const money =
      String(value || '')
        .replace(
          /[^0-9,.-]/g,
          ''
        );


    if (!money) {
      return null;
    }


    const lastDot =
      money.lastIndexOf('.');


    const lastComma =
      money.lastIndexOf(',');


    const decimalIndex =
      Math.max(
        lastDot,
        lastComma
      );


    let normalized;


    if (
      decimalIndex > -1
    ) {
      normalized =
        `${money
          .slice(
            0,
            decimalIndex
          )
          .replace(
            /[.,]/g,
            ''
          )
        }.${money
          .slice(
            decimalIndex + 1
          )
          .replace(
            /[.,]/g,
            ''
          )
        }`;
    }

    else {
      normalized =
        money.replace(
          /[.,]/g,
          ''
        );
    }


    const amount =
      Number(normalized);


    return (
      Number.isFinite(amount) &&
      amount > 0
    )
      ? amount
      : null;
  }


  formatCurrency(amount) {
    try {
      return new Intl.NumberFormat(
        undefined,
        {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      ).format(amount);
    }

    catch (error) {
      return `€${amount.toFixed(2)}`;
    }
  }


  normalize(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }
}


if (
  !customElements.get(
    'pm-custom-stickers-order-summary'
  )
) {
  customElements.define(
    'pm-custom-stickers-order-summary',
    PMCustomStickersOrderSummary
  );
}